import { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft,
  Save,
  ShieldCheck,
  Printer,
  AlertCircle,
  X,
} from "lucide-react";
import api, { formatErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import VoiceTextarea from "../components/VoiceTextarea";
import StepIndex, { PrevNextBar, loadDraft, saveDraft, clearDraft } from "../components/StepIndex";
import DatePickerInput from "../components/DatePickerInput";
import { ONBOARDING_SECTIONS, ONBOARDING_FIELD_COUNT } from "../lib/onboardingSchema";
import { printElement } from "../lib/printArea";

/**
 * Full-page Lead Onboarding form (Wave 4 #52).
 * Pre-fills from the existing lead, customer, and primary site.
 * Saves to /api/leads/{id}/onboarding (PATCH).
 *
 * 2026-05-13 — Sign-off removed (Nuno feedback). Onboarding is now a
 * pure info-gathering form that is ALWAYS editable. No locking, no
 * signature, no "finalise" step. Edits autosave + manual Save draft.
 */
export default function LeadOnboardingForm() {
  const { id: leadId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [lead, setLead] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [site, setSite] = useState(null);
  const [data, setData] = useState({});
  const [activeKey, setActiveKey] = useState(ONBOARDING_SECTIONS[0].key);
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [err, setErr] = useState("");

  // ---- Load lead + linked customer/site ----
  useEffect(() => {
    (async () => {
      try {
        const l = await api.get(`/leads`);
        const found = (l.data || []).find((x) => x.id === leadId);
        if (!found) {
          setErr("Lead not found.");
          return;
        }
        setLead(found);
        setData(found.onboarding || {});
        if (found.customer_id) {
          const c = await api.get(`/customers/${found.customer_id}`);
          setCustomer(c.data);
        }
        if (found.site_id) {
          try {
            const s = await api.get(`/sites?customer_id=${found.customer_id}`);
            setSite((s.data || []).find((x) => x.id === found.site_id));
          } catch (_e) {
            // ignore
          }
        }
      } catch (e) {
        setErr(formatErr(e?.response?.data?.detail, "Could not load lead"));
      }
    })();
  }, [leadId]);

  // ---- Pre-fill on first load ----
  useEffect(() => {
    if (!lead || Object.keys(data).length > 0) return; // already loaded data — skip
    const seeded = {};
    ONBOARDING_SECTIONS.forEach((sec) => {
      const fields = sec.fields || [];
      fields.forEach((f) => {
        if (f.pre_fill) {
          const v = f.pre_fill(lead, customer, site);
          if (v) seeded[`${sec.key}.${f.key}`] = v;
        }
      });
      // Repeat-group fields (e.g. owner.name, manager.phone, etc.)
      (sec.repeat_groups || []).forEach((g) => {
        (g.fields || []).forEach((f) => {
          if (f.pre_fill) {
            const v = f.pre_fill(lead, customer, site, g.prefix);
            if (v) seeded[`${sec.key}.${g.prefix}_${f.key}`] = v;
          }
        });
      });
    });
    if (Object.keys(seeded).length) setData((d) => ({ ...d, ...seeded }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead, customer, site]);

  // 2026-05-13 — Onboarding never locks. Field always editable.
  const isLocked = false;

  const setField = useCallback(
    (sectionKey, fieldKey, value) => {
      setData((d) => ({ ...d, [`${sectionKey}.${fieldKey}`]: value }));
      setSavedAt(null);
    },
    [],
  );

  // ---- Filled count for progress bar ----
  const filled = useMemo(() => {
    let n = 0;
    Object.values(data).forEach((v) => {
      if (v === null || v === undefined || v === "") return;
      if (Array.isArray(v) && v.length === 0) return;
      n += 1;
    });
    return n;
  }, [data]);
  const completionPct = Math.min(100, Math.round((filled / ONBOARDING_FIELD_COUNT) * 100));

  // Per-section completion lookup (for the StepIndex tick marks).
  const sectionComplete = useCallback(
    (sectionKey) => {
      const sec = ONBOARDING_SECTIONS.find((s) => s.key === sectionKey);
      if (!sec) return false;
      const fields = sec.fields || [];
      if (fields.length === 0) return false;
      const filledCount = fields.filter((f) => {
        const v = data[`${sectionKey}.${f.key}`];
        if (v === null || v === undefined || v === "") return false;
        if (Array.isArray(v) && v.length === 0) return false;
        return true;
      }).length;
      // 80% threshold = "section complete" tick
      return filledCount / fields.length >= 0.8;
    },
    [data],
  );

  // ---- localStorage draft auto-save (debounced) ----
  const draftKey = leadId ? `hv:onboarding:lead:${leadId}` : null;
  // Hydrate from draft if backend has nothing AND draft exists
  useEffect(() => {
    if (!draftKey || !lead) return;
    if (Object.keys(data).length > 0) return;
    if (lead.onboarding && Object.keys(lead.onboarding).length > 0) return;
    const d = loadDraft(draftKey);
    if (d?.value && typeof d.value === "object") {
      setData(d.value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftKey, lead]);
  // Persist to localStorage whenever data changes (cheap; small payload)
  useEffect(() => {
    if (!draftKey) return;
    if (Object.keys(data).length === 0) return;
    saveDraft(draftKey, data);
  }, [data, draftKey]);

  // ---- Save handlers ----
  const save = async (extras = {}) => {
    setBusy(true);
    setErr("");
    try {
      const payload = { onboarding: data, ...extras };
      const r = await api.patch(`/leads/${leadId}/onboarding`, payload);
      setLead(r.data);
      setSavedAt(new Date().toISOString());
      // Once persisted to backend, drop the local draft
      if (draftKey) clearDraft(draftKey);
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail, "Could not save"));
    } finally {
      setBusy(false);
    }
  };

  // 2026-05-13 — `finalise` and `reopen` removed; onboarding never
  // locks. If older leads have `onboarding_completed_at` set, the field
  // is simply ignored by the UI now.

  const onPrint = () => {
    // Defer one tick so React renders the full printable container first
    requestAnimationFrame(() => {
      printElement("onboarding-print-area", {
        title: `Onboarding — ${customer?.name || lead?.title || "Prospect"}`,
      });
    });
  };

  if (err && !lead) {
    return (
      <div className="hv-card p-8 text-center">
        <AlertCircle className="w-8 h-8 text-rose-400 mx-auto mb-3" />
        <div className="text-sm text-rose-300">{err}</div>
        <Link to="/leads" className="hv-btn hv-btn-ghost mt-4">
          <ArrowLeft className="w-4 h-4" /> Back to leads
        </Link>
      </div>
    );
  }
  if (!lead) {
    return <div className="text-center text-[#647087] p-8">Loading…</div>;
  }

  const activeSection = ONBOARDING_SECTIONS.find((s) => s.key === activeKey);

  return (
    <div className="space-y-4 hv-fade-in" data-testid="lead-onboarding-page">
      {/* ---- Top header ---- */}
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <button
            type="button"
            onClick={() => navigate(`/leads`)}
            className="hv-btn hv-btn-ghost text-xs mb-2"
            data-testid="onboarding-close"
            title="Close without saving"
          >
            <X className="w-4 h-4" /> Close
          </button>
          <h1 className="font-display text-2xl md:text-4xl font-bold tracking-tight flex items-center gap-3 flex-wrap">
            <ShieldCheck className="w-7 h-7 text-[#1FB6C1]" />
            Lead Onboarding
            <span
              className="text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded-full border border-[#1FB6C1]/40 bg-[#1FB6C1]/10 text-[#1FB6C1] inline-flex items-center gap-1"
              data-testid="onboarding-editable-badge"
              title="This form is always editable — no sign-off step"
            >
              Always editable
            </span>
          </h1>
          <div className="text-xs text-[#a0aabf] mt-1 truncate">
            {lead.title || "—"} · {customer?.name || lead.customer_name || "Prospect"}
            {customer?.external_ref ? ` · ${customer.external_ref}` : ""}
          </div>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <div
            className="text-[10px] uppercase tracking-wider text-[#647087] font-mono"
            data-testid="onboarding-saved-status"
          >
            {savedAt ? `Saved ${new Date(savedAt).toLocaleTimeString('en-GB')}` : "Not saved yet"}
          </div>
          <button
            type="button"
            onClick={onPrint}
            className="hv-btn hv-btn-ghost text-xs"
            data-testid="onboarding-print"
          >
            <Printer className="w-3.5 h-3.5" /> Export PDF
          </button>
          <button
            type="button"
            onClick={() => save()}
            disabled={busy}
            className="hv-btn hv-btn-primary text-xs"
            data-testid="onboarding-save"
          >
            <Save className="w-3.5 h-3.5" /> {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </header>

      {err && (
        <div className="hv-card border-rose-500/40 bg-rose-500/5 p-3 text-xs text-rose-300">
          {err}
        </div>
      )}

      {/* ---- Body: section nav (left) + form (right) ---- */}
      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4">
        {/* Section navigation — shared StepIndex */}
        <div className="self-start">
          <StepIndex
            sections={ONBOARDING_SECTIONS.map((s) => ({ key: s.key, title: s.title }))}
            activeKey={activeKey}
            onJump={setActiveKey}
            completionPct={completionPct}
            isComplete={sectionComplete}
            testId="onboarding-step-index"
          />
        </div>

        {/* Active section form */}
        <div>
          <SectionEditor
            section={activeSection}
            data={data}
            setField={setField}
            isLocked={isLocked}
          />
          <PrevNextBar
            sections={ONBOARDING_SECTIONS.map((s) => ({ key: s.key, title: s.title }))}
            activeKey={activeKey}
            onJump={setActiveKey}
            testId="onboarding-step-nav"
          />
        </div>
      </div>

      {/* Hidden printable region — full document for PDF export. Always
          rendered but kept off-screen until printElement copies it. */}
      <div
        id="onboarding-print-area"
        style={{ position: "absolute", left: "-99999px", top: 0, width: "210mm" }}
        aria-hidden="true"
      >
        <PrintableDoc
          lead={lead}
          customer={customer}
          site={site}
          data={data}
          user={user}
        />
      </div>
    </div>
  );
}

/* ============================================================================
 * SectionEditor — renders all fields/repeat groups for one section
 * ========================================================================== */
function SectionEditor({ section, data, setField, isLocked }) {
  if (!section) return null;
  const get = (k) => data[`${section.key}.${k}`] ?? "";

  // Conditional show check
  const shouldShow = (f) => {
    if (!f.show_if) return true;
    return Object.entries(f.show_if).every(([k, expected]) => {
      const v = get(k);
      if (Array.isArray(expected)) return expected.includes(v);
      return v === expected;
    });
  };

  return (
    <section
      className="hv-card p-4 md:p-6 space-y-5"
      data-testid={`onboarding-section-${section.key}`}
    >
      <div>
        <h2 className="font-display text-xl md:text-2xl font-bold tracking-tight">
          {section.title}
        </h2>
        {section.description && (
          <div className="text-xs text-[#a0aabf] mt-1">{section.description}</div>
        )}
      </div>

      {/* Repeat groups (e.g. owner / manager / maint contacts) */}
      {(section.repeat_groups || []).map((g) => (
        <div
          key={g.prefix}
          className="border border-white/10 rounded p-3 md:p-4 space-y-3"
          data-testid={`onboarding-group-${section.key}-${g.prefix}`}
        >
          <div className="text-xs uppercase tracking-wider text-[#1FB6C1] font-bold">
            {g.title}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {g.fields.map((f) => (
              <FieldEditor
                key={`${g.prefix}_${f.key}`}
                field={{ ...f, key: `${g.prefix}_${f.key}` }}
                value={get(`${g.prefix}_${f.key}`)}
                onChange={(v) => setField(section.key, `${g.prefix}_${f.key}`, v)}
                disabled={isLocked}
              />
            ))}
          </div>
        </div>
      ))}

      {/* Standalone fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {(section.fields || []).filter(shouldShow).map((f) => (
          <FieldEditor
            key={f.key}
            field={f}
            value={get(f.key)}
            onChange={(v) => setField(section.key, f.key, v)}
            disabled={isLocked}
          />
        ))}
      </div>
    </section>
  );
}

/* ============================================================================
 * FieldEditor — one field row (typed via field.type)
 * ========================================================================== */
function FieldEditor({ field, value, onChange, disabled }) {
  const tid = `onboarding-field-${field.key}`;
  const lab = (
    <label className="block text-[10px] uppercase tracking-wider text-[#647087] mb-1 font-bold">
      {field.label} {field.required && <span className="text-rose-400">*</span>}
    </label>
  );
  const wrap = (children, full = false) => (
    <div className={full ? "md:col-span-2" : ""}>
      {lab}
      {children}
    </div>
  );

  switch (field.type) {
    case "textarea":
      return wrap(
        <VoiceTextarea
          value={value || ""}
          onChange={onChange}
          disabled={disabled}
          rows={4}
          placeholder={field.placeholder}
          className="text-sm w-full"
          context="lead_activity"
          testIdPrefix={`${tid}-voice`}
          data-testid={tid}
        />,
        true,
      );
    case "number":
      return wrap(
        <input
          type="number"
          step="any"
          value={value === 0 ? 0 : value || ""}
          onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
          disabled={disabled}
          placeholder={field.placeholder}
          className="hv-input w-full text-sm"
          data-testid={tid}
        />,
      );
    case "date":
      return wrap(
        <DatePickerInput
          value={value || ""}
          onChange={onChange}
          disabled={disabled}
          testId={tid}
        />,
      );
    case "select":
      return wrap(
        <select
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className="hv-input w-full text-sm"
          data-testid={tid}
        >
          <option value="">— pick —</option>
          {field.options.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>,
      );
    case "radio":
      return wrap(
        <div className="flex flex-wrap gap-2" data-testid={tid}>
          {field.options.map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => onChange(value === opt ? "" : opt)}
              disabled={disabled}
              className={`px-3 py-1.5 rounded border text-xs transition ${
                value === opt
                  ? "border-[#1FB6C1] bg-[#1FB6C1]/15 text-[#1FB6C1]"
                  : "border-white/10 text-[#a0aabf] hover:border-[#1FB6C1]/40"
              } disabled:opacity-50 disabled:cursor-not-allowed`}
              data-testid={`${tid}-${opt}`}
            >
              {opt}
            </button>
          ))}
        </div>,
      );
    case "multiselect": {
      const arr = Array.isArray(value) ? value : [];
      const toggle = (opt) =>
        arr.includes(opt)
          ? onChange(arr.filter((x) => x !== opt))
          : onChange([...arr, opt]);
      return wrap(
        <div className="flex flex-wrap gap-2" data-testid={tid}>
          {field.options.map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => toggle(opt)}
              disabled={disabled}
              className={`px-3 py-1.5 rounded border text-xs transition ${
                arr.includes(opt)
                  ? "border-[#A78BFA] bg-[#A78BFA]/15 text-[#A78BFA]"
                  : "border-white/10 text-[#a0aabf] hover:border-[#A78BFA]/40"
              } disabled:opacity-50 disabled:cursor-not-allowed`}
              data-testid={`${tid}-${opt}`}
            >
              {opt}
            </button>
          ))}
        </div>,
        true,
      );
    }
    case "text":
    default:
      return wrap(
        <input
          type="text"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          placeholder={field.placeholder}
          className="hv-input w-full text-sm"
          data-testid={tid}
        />,
      );
  }
}


/* ============================================================================
 * PrintableDoc — clean A4-friendly export. Uses the printArea pcard/ptext
 * classes so the output is always white-on-black-readable.
 * ========================================================================== */
function PrintableDoc({ lead, customer, site, data, user }) {
  const get = (sec, k) => {
    const v = data[`${sec}.${k}`];
    if (Array.isArray(v)) return v.join(", ");
    return v ?? "";
  };
  const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB') : "—");
  return (
    <div className="pcard ptext" style={{ padding: "8mm", fontSize: "11pt", lineHeight: 1.45 }}>
      {/* Brand header */}
      <div
        style={{
          borderBottom: "2px solid #0d6e75",
          paddingBottom: "6mm",
          marginBottom: "6mm",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
        }}
      >
        <div>
          <div className="paccent" style={{ fontSize: "9pt", letterSpacing: "0.2em", fontWeight: 700 }}>
            HIDROVERSE · UNIVERSAL WATER SYSTEMS
          </div>
          <div style={{ fontSize: "20pt", fontWeight: 800 }}>Prospect Onboarding Sheet</div>
          <div className="pmeta" style={{ fontSize: "10pt" }}>
            {customer?.name || lead?.customer_name || lead?.title || "Prospect"}
            {customer?.external_ref ? `  ·  ${customer.external_ref}` : ""}
          </div>
        </div>
        <div className="pmeta" style={{ fontSize: "9pt", textAlign: "right" }}>
          <div>Document date: {new Date().toLocaleDateString('en-GB')}</div>
          <div>Compiled by: {user?.name || "—"}</div>
        </div>
      </div>

      {/* Lead summary chips */}
      <div className="pmeta" style={{ fontSize: "10pt", marginBottom: "5mm" }}>
        <strong>Lead title:</strong> {lead?.title || "—"} &nbsp;·&nbsp;
        <strong>Type:</strong> {lead?.lead_title_enum || "—"} &nbsp;·&nbsp;
        <strong>Phase:</strong> {lead?.phase || "—"} &nbsp;·&nbsp;
        <strong>Source:</strong> {lead?.lead_source || "—"} &nbsp;·&nbsp;
        <strong>Bracket:</strong> {lead?.price_bracket || "—"} &nbsp;·&nbsp;
        <strong>Current provider:</strong> {lead?.current_provider || "—"}
      </div>

      {/* Sections */}
      {ONBOARDING_SECTIONS.map((sec) => {
        const fields = sec.fields || [];
        const groups = sec.repeat_groups || [];
        return (
          <section key={sec.key} className="avoid-break" style={{ marginBottom: "6mm" }}>
            <h2
              className="paccent pdivider"
              style={{
                fontSize: "13pt",
                fontWeight: 800,
                borderBottom: "1px solid #c5cad3",
                paddingBottom: "1.5mm",
                marginBottom: "3mm",
              }}
            >
              {sec.title}
            </h2>
            {sec.description && (
              <div className="pmeta" style={{ fontSize: "9pt", marginBottom: "2mm", fontStyle: "italic" }}>
                {sec.description}
              </div>
            )}
            {groups.map((g) => (
              <div key={g.prefix} style={{ marginBottom: "3mm" }}>
                <div className="plabel" style={{ fontSize: "10pt", marginBottom: "1mm" }}>
                  {g.title}
                </div>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "10pt" }}>
                  <tbody>
                    {g.fields.map((f) => (
                      <tr key={f.key} className="pdivider">
                        <td className="plabel" style={{ width: "32%", padding: "1mm 2mm", borderBottom: "1px solid #e3e6ec" }}>
                          {f.label}
                        </td>
                        <td className="ptext" style={{ padding: "1mm 2mm", borderBottom: "1px solid #e3e6ec" }}>
                          {get(sec.key, `${g.prefix}_${f.key}`) || "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "10pt" }}>
              <tbody>
                {fields.map((f) => (
                  <tr key={f.key} className="pdivider">
                    <td className="plabel" style={{ width: "32%", padding: "1mm 2mm", borderBottom: "1px solid #e3e6ec", verticalAlign: "top" }}>
                      {f.label}
                    </td>
                    <td className="ptext" style={{ padding: "1mm 2mm", borderBottom: "1px solid #e3e6ec", whiteSpace: "pre-wrap" }}>
                      {get(sec.key, f.key) || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        );
      })}
    </div>
  );
}
